import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { 
  FaBullhorn, FaPlus, FaSearch, FaTrash, FaTimes, FaCheck, 
  FaArrowLeft, FaArrowRight, FaPaperPlane, FaSpinner, 
  FaFilePdf, FaImage, FaTag, FaCalendarAlt, FaEye, FaCheckDouble
} from 'react-icons/fa';
import { crmMarketingCampaignApi, crmLeadApi, crmWhatsAppApi } from '../../api/crmApi';
import './CRM.css';

const MARKETING_TEMPLATES = [
  {
    id: 'new_customer',
    name: 'New Customer',
    type: 'NEW_CUSTOMER',
    category: 'Onboarding',
    icon: FaFilePdf,
    description: 'Welcome new leads with combined brochure & visiting card PDF',
    requiresPdf: true,
    requiresImage: false,
    fields: []
  },
  {
    id: 'product_arrival',
    name: 'Product Arrival',
    type: 'PRODUCT_ARRIVAL',
    category: 'Product Alert',
    icon: FaImage,
    description: 'Notify customers about new product arrivals with product image',
    requiresPdf: false,
    requiresImage: true,
    fields: [
      { key: 'productId', label: 'Product', type: 'select', required: true },
      { key: 'productImageUrl', label: 'Product Image URL', type: 'url', required: false },
    ]
  },
  {
    id: 'special_offer',
    name: 'Special Offer',
    type: 'SPECIAL_OFFER',
    category: 'Promotion',
    icon: FaTag,
    description: 'Promote special offers with discount, expiry date & product image',
    requiresPdf: false,
    requiresImage: true,
    fields: [
      { key: 'productId', label: 'Product', type: 'select', required: true },
      { key: 'productImageUrl', label: 'Product Image URL', type: 'url', required: false },
      { key: 'discount', label: 'Discount (e.g., 20%)', type: 'text', required: true },
      { key: 'offerExpiryDate', label: 'Offer Expiry Date', type: 'date', required: true },
    ]
  }
];

const STEPS = ['Select Template', 'Configure Content', 'Select Recipients', 'Review & Send'];
const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMMessages() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [campaignName, setCampaignName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({});
  const [previewData, setPreviewData] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, l] = await Promise.allSettled([
        crmMarketingCampaignApi.getAll({ size: 100 }),
        crmWhatsAppApi.getTemplates(),
        crmLeadApi.getAll({ size: 500 })
      ]);
      if (c.status === 'fulfilled') {
        const d = Array.isArray(c.value?.data) ? c.value.data : c.value?.data?.content;
        if (d?.length) setCampaigns(d);
      }
      if (t.status === 'fulfilled') {
        const d = Array.isArray(t.value?.data) ? t.value.data : t.value?.data?.content;
        if (d?.length) setTemplates(d);
      }
      if (l.status === 'fulfilled') {
        const d = Array.isArray(l.value?.data) ? l.value.data : l.value?.data?.content;
        if (d?.length) setLeads(d.filter(lead => lead.mobile && lead.mobile.trim()));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleRecipient = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const selectAllRecipients = () => setSelectedIds(leads.map(l => l.id));
  const clearRecipients = () => setSelectedIds([]);

  const startWizard = () => { 
    setWizard(true); 
    setStep(0); 
    setCampaignName(''); 
    setSelectedTemplate(null); 
    setSelectedIds([]); 
    setFormData({});
    setPreviewData({});
  };

  const handleFormChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    updatePreview();
  };

  const updatePreview = () => {
    if (!selectedTemplate) return;
    
    let preview = {};
    switch (selectedTemplate.type) {
      case 'NEW_CUSTOMER':
        preview = {
          customerName: 'Ankit Mehta',
          company: 'Mehta Oxygen Pvt. Ltd.',
          product: formData.requirement || 'LPG Cylinders',
          provider: 'IndiaMART',
          pdfAttached: true
        };
        break;
      case 'PRODUCT_ARRIVAL':
        preview = {
          customerName: 'Ankit Mehta',
          productName: formData.productName || 'LPG XL Cylinder',
          companyName: 'Sahil Enterprises',
          imageAttached: !!formData.productImageUrl
        };
        break;
      case 'SPECIAL_OFFER':
        preview = {
          customerName: 'Ankit Mehta',
          productName: formData.productName || 'LPG XL Cylinder',
          discount: formData.discount || '20%',
          expiryDate: formData.offerExpiryDate || '2026-12-31',
          imageAttached: !!formData.productImageUrl
        };
        break;
      default:
        preview = {};
        break;
    }
    setPreviewData(preview);
  };

  const validateStep = () => {
    if (step === 0) {
      if (!campaignName.trim()) { toast.error('Enter a campaign name.'); return false; }
      if (!selectedTemplate) { toast.error('Select a template.'); return false; }
    }
    if (step === 1) {
      if (selectedTemplate?.requiresPdf && !formData.pdfUrl) { 
        toast.error('Please provide the combined brochure PDF URL.'); return false; 
      }
      if (selectedTemplate?.requiresImage && !formData.productImageUrl) { 
        toast.error('Please provide a product image URL.'); return false; 
      }
      if (selectedTemplate?.type === 'SPECIAL_OFFER') {
        if (!formData.discount) { toast.error('Please enter discount percentage.'); return false; }
        if (!formData.offerExpiryDate) { toast.error('Please select offer expiry date.'); return false; }
      }
    }
    if (step === 2) {
      if (!selectedIds.length) { toast.error('Select at least one recipient.'); return false; }
    }
    return true;
  };

  const sendCampaign = async () => {
    if (!validateStep()) return;
    setSending(true);
    
    const payload = {
      name: campaignName,
      templateType: selectedTemplate.type,
      templateName: selectedTemplate.id,
      productId: formData.productId,
      productName: formData.productName,
      productImageUrl: formData.productImageUrl,
      discount: formData.discount,
      offerExpiryDate: formData.offerExpiryDate,
      pdfUrl: formData.pdfUrl,
      recipientIds: selectedIds,
      status: 'DRAFT',
      notes: formData.companyName || ''
    };
    
    try {
      const res = await crmMarketingCampaignApi.create(payload);
      const newId = res.data?.id || res.data;
      if (newId) {
        await crmMarketingCampaignApi.send(newId);
        toast.success(`Campaign "${campaignName}" sent to ${selectedIds.length} recipients.`);
      } else {
        toast.success(`Campaign created. Processing ${selectedIds.length} recipients in background.`);
      }
      setWizard(false);
      fetchAll();
    } catch (error) {
      console.error('Campaign error:', error);
      toast.error('Failed to send campaign — check API configuration.');
    } finally {
      setSending(false);
    }
  };

  const scheduleCampaign = async () => {
    if (!validateStep()) return;
    if (!formData.scheduledAt) { toast.error('Select schedule date & time.'); return; }
    
    const payload = {
      name: campaignName,
      templateType: selectedTemplate.type,
      templateName: selectedTemplate.id,
      productId: formData.productId,
      productName: formData.productName,
      productImageUrl: formData.productImageUrl,
      discount: formData.discount,
      offerExpiryDate: formData.offerExpiryDate,
      pdfUrl: formData.pdfUrl,
      recipientIds: selectedIds,
      status: 'SCHEDULED',
      scheduledAt: formData.scheduledAt,
      notes: formData.companyName || ''
    };
    
    try {
      await crmMarketingCampaignApi.create(payload);
      toast.success(`Campaign "${campaignName}" scheduled for ${fmt(formData.scheduledAt)}.`);
      setWizard(false);
      fetchAll();
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error('Failed to schedule campaign.');
    }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try { await crmMarketingCampaignApi.delete(c.id); } catch { /* ignore */ }
    setCampaigns((p) => p.filter((x) => x.id !== c.id));
    toast.success('Campaign deleted.');
  };

  const filteredLeads = leads.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.customerName || '').toLowerCase().includes(q) || 
           (r.company || '').toLowerCase().includes(q) || 
           (r.mobile || '').includes(q);
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      'DRAFT': 'crm-badge-new',
      'SCHEDULED': 'crm-badge-contacted',
      'SENDING': 'crm-badge-contacted',
      'SENT': 'crm-badge-won',
      'COMPLETED': 'crm-badge-won',
      'FAILED': 'crm-badge-lost'
    };
    return statusMap[status] || 'crm-badge-new';
  };

  const renderTemplateConfig = () => {
    if (!selectedTemplate) return null;
    
    const template = selectedTemplate;
    return (
      <div>
        <div className="crm-field" style={{ marginBottom: 16 }}>
          <label>Campaign Name <span className="req">*</span></label>
          <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. Diwali Offer 2026" />
        </div>
        
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-soft)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <template.icon style={{ color: 'var(--primary)', fontSize: 20 }} />
            <strong>{template.name}</strong>
            <span className="crm-badge crm-badge-new" style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{template.category}</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>{template.description}</div>
        </div>

        {template.type === 'NEW_CUSTOMER' && (
          <div className="crm-field" style={{ marginBottom: 16 }}>
            <label>Combined Brochure + Visiting Card PDF URL <span className="req">*</span></label>
            <input 
              type="url" 
              value={formData.pdfUrl || ''} 
              onChange={(e) => handleFormChange('pdfUrl', e.target.value)} 
              placeholder="https://example.com/brochure_visiting_card.pdf" 
            />
            <small style={{ color: 'var(--ink-soft)', fontSize: '0.75rem' }}>
              Single PDF containing both product brochure and visiting card. 
              The PDF will be attached to the WhatsApp message.
            </small>
          </div>
        )}

        {template.type === 'PRODUCT_ARRIVAL' && (
          <>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Product <span className="req">*</span></label>
              <select 
                value={formData.productId || ''} 
                onChange={(e) => {
                  const selected = leads.find(l => l.id === e.target.value);
                  handleFormChange('productId', e.target.value);
                  if (selected) handleFormChange('productName', selected.product || selected.requirement);
                }}
              >
                <option value="">Select Product</option>
                {leads.filter(l => l.product).map(l => (
                  <option key={l.id} value={l.id}>{l.product} - {l.customerName}</option>
                ))}
              </select>
            </div>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Product Image URL <span className="req">*</span></label>
              <input 
                type="url" 
                value={formData.productImageUrl || ''} 
                onChange={(e) => handleFormChange('productImageUrl', e.target.value)} 
                placeholder="https://example.com/product_image.jpg" 
              />
              <small style={{ color: 'var(--ink-soft)', fontSize: '0.75rem' }}>
                Image will be sent as WhatsApp image header. Recommended: 400x400px, max 5MB.
              </small>
            </div>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Company Name (for template)</label>
              <input 
                value={formData.companyName || ''} 
                onChange={(e) => handleFormChange('companyName', e.target.value)} 
                placeholder="Sahil Enterprises" 
              />
            </div>
          </>
        )}

        {template.type === 'SPECIAL_OFFER' && (
          <>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Product <span className="req">*</span></label>
              <select 
                value={formData.productId || ''} 
                onChange={(e) => {
                  const selected = leads.find(l => l.id === e.target.value);
                  handleFormChange('productId', e.target.value);
                  if (selected) handleFormChange('productName', selected.product || selected.requirement);
                }}
              >
                <option value="">Select Product</option>
                {leads.filter(l => l.product).map(l => (
                  <option key={l.id} value={l.id}>{l.product} - {l.customerName}</option>
                ))}
              </select>
            </div>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Product Image URL <span className="req">*</span></label>
              <input 
                type="url" 
                value={formData.productImageUrl || ''} 
                onChange={(e) => handleFormChange('productImageUrl', e.target.value)} 
                placeholder="https://example.com/offer_image.jpg" 
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="crm-field">
                <label>Discount <span className="req">*</span></label>
                <input 
                  type="text" 
                  value={formData.discount || ''} 
                  onChange={(e) => handleFormChange('discount', e.target.value)} 
                  placeholder="e.g., 20% or Flat ₹500" 
                />
              </div>
              <div className="crm-field">
                <label>Offer Expiry Date <span className="req">*</span></label>
                <input 
                  type="date" 
                  value={formData.offerExpiryDate || ''} 
                  onChange={(e) => handleFormChange('offerExpiryDate', e.target.value)} 
                />
              </div>
            </div>
            <div className="crm-field" style={{ marginBottom: 16 }}>
              <label>Company Name (for template)</label>
              <input 
                value={formData.companyName || ''} 
                onChange={(e) => handleFormChange('companyName', e.target.value)} 
                placeholder="Sahil Enterprises" 
              />
            </div>
          </>
        )}

        <div className="crm-field" style={{ marginBottom: 16 }}>
          <label>Schedule for Later (optional)</label>
          <input 
            type="datetime-local" 
            value={formData.scheduledAt || ''} 
            onChange={(e) => handleFormChange('scheduledAt', e.target.value)} 
          />
          <small style={{ color: 'var(--ink-soft)', fontSize: '0.75rem' }}>
            Leave empty to send immediately.
          </small>
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    if (!selectedTemplate) return <div style={{ color: 'var(--ink-soft)' }}>Select a template to preview</div>;
    
    const template = selectedTemplate;
    let previewContent = '';
    
    switch (template.type) {
      case 'NEW_CUSTOMER':
        previewContent = `Hi *${previewData.customerName || 'Customer Name'}* 👋

Welcome to Sahil Enterprises! 🎉

Thank you for your interest in our *${previewData.product || 'products'}*.

📄 *Brochure & Visiting Card* attached with this message.

🏢 *Company*
Sahil Enterprises

📞 *Contact*
+91-XXXXXXXXXX

✉ *Email*
info@sahilenterprises.com

Thank you for choosing us!`;
        break;
      case 'PRODUCT_ARRIVAL':
        previewContent = `Hi *${previewData.customerName || 'Customer Name'}* 👋

Great news! Our new *${previewData.productName || 'Product'}* has arrived! 📦

🏢 *Company*
${previewData.companyName || 'Sahil Enterprises'}

${previewData.imageAttached ? '📸 *Product image attached*' : ''}

Reply INTEREST for more details or to place an order.`;
        break;
      case 'SPECIAL_OFFER':
        previewContent = `Hi *${previewData.customerName || 'Customer Name'}* 👋

🎉 *Special Offer Alert!* 🎉

Get *${previewData.discount || '20%'} OFF* on *${previewData.productName || 'Product'}*!

⏰ Offer valid till: *${previewData.expiryDate ? new Date(previewData.expiryDate).toLocaleDateString('en-IN') : 'DD-MM-YYYY'}*

🏢 *Company*
${formData.companyName || 'Sahil Enterprises'}

${previewData.imageAttached ? '📸 *Product image attached*' : ''}

👉 Tap "Order Now" to claim your discount!

Thank you!`;
        break;
      default:
        previewContent = '';
        break;
    }
    
    return (
      <div style={{ background: '#DCF8C6', borderRadius: 14, padding: '10px 14px', fontSize: '0.83rem', maxWidth: 420, position: 'relative', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {previewContent}
        <div style={{ fontSize: '0.66rem', textAlign: 'right', opacity: 0.6, marginTop: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
          now <FaCheckDouble aria-hidden="true" />
        </div>
      </div>
    );
  };

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaBullhorn aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">Marketing Campaigns</h1>
            <p className="crm-subtitle">New Customer · Product Arrival · Special Offer</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-primary" onClick={startWizard}><FaPlus aria-hidden="true" /> New Campaign</button>
        </div>
      </div>

      <div className="crm-table-wrap">
        <div className="crm-table-head"><h3 className="crm-table-title"><FaBullhorn aria-hidden="true" /> Campaigns</h3></div>
        <table className="crm-table">
          <thead><tr><th>Campaign</th><th>Template</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Delivered</th><th>Read</th><th>Failed</th><th>Sent / Scheduled</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 3 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 10 }).map((__, c) => (
                    <td key={c}><SkeletonBlock width={c === 0 ? 150 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>
                      Type: {c.templateType} | Template: {c.templateName}
                    </div>
                  </td>
                  <td>
                    <span style={{ 
                      background: c.templateType === 'NEW_CUSTOMER' ? '#DCFCE7' : 
                                  c.templateType === 'PRODUCT_ARRIVAL' ? '#DBEAFE' : '#FEF3C7',
                      color: c.templateType === 'NEW_CUSTOMER' ? '#15803D' : 
                             c.templateType === 'PRODUCT_ARRIVAL' ? '#2563EB' : '#D97706',
                      padding: '2px 8px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600
                    }}>
                      {c.templateType}
                    </span>
                  </td>
                  <td><span className={`crm-badge ${getStatusBadge(c.status)}`}>{c.status}</span></td>
                  <td>{c.recipientCount}</td>
                  <td>{c.sentCount ?? 0}</td>
                  <td>{c.deliveredCount ?? 0}</td>
                  <td>{c.readCount ?? 0}</td>
                  <td>{c.failedCount ?? 0}</td>
                  <td>{fmt(c.sentAt || c.scheduledAt)}</td>
                  <td><div className="crm-actions">
                    {c.status === 'DRAFT' && (
                      <button
                        type="button"
                        className="crm-btn crm-btn-sm crm-btn-primary"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await crmMarketingCampaignApi.send(c.id);
                          } catch {
                            toast.error('Failed to send campaign.');
                            return;
                          }
                          setCampaigns((p) => p.map((x) => x.id === c.id ? { ...x, status: 'SENDING', sentAt: new Date().toISOString() } : x));
                          toast.success('Campaign sending started.');
                        }}
                      >Send</button>
                    )}
                    {c.status === 'SCHEDULED' && (
                      <span className="crm-badge crm-badge-contacted" style={{ cursor: 'default' }}>Scheduled</span>
                    )}
                    <button type="button" className="crm-icon-btn" title="View Details" aria-label="View Details" onClick={() => window.open(`/crm/marketing-campaigns/${c.id}`, '_blank')}><FaEye aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn lost" title="Delete" aria-label="Delete campaign" onClick={() => deleteCampaign(c)}><FaTrash aria-hidden="true" /></button>
                  </div></td>
                </tr>
              ))}
              {!campaigns.length && <tr><td colSpan={10}><div className="crm-empty"><div className="crm-empty-icon"><FaBullhorn aria-hidden="true" /></div><div className="crm-empty-title">No campaigns yet</div><div className="crm-empty-desc">Create your first marketing campaign for New Customer onboarding, Product Arrival alerts, or Special Offers.</div></div></td></tr>}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* WIZARD */}
      {wizard && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setWizard(false)}>
          <div className="crm-modal crm-modal-xl" role="dialog" aria-modal="true" aria-label="New marketing campaign">
            <div className="crm-modal-header">
              <h3><FaBullhorn aria-hidden="true" /> New Marketing Campaign</h3>
              <button type="button" className="crm-modal-close" onClick={() => setWizard(false)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>

            <div className="crm-steps">
              {STEPS.map((s, i) => (
                <div key={s} className={`crm-step ${step === i ? 'active' : step > i ? 'done' : ''}`}>
                  <span className="crm-step-num">{step > i ? <FaCheck aria-hidden="true" /> : i + 1}</span> {s}
                </div>
              ))}
            </div>

            <div className="crm-modal-body">
              {step === 0 && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 12 }}>Select Marketing Template</label>
                  <div className="crm-list-grid" style={{ marginTop: 8 }}>
                    {MARKETING_TEMPLATES.map((t) => (
                      <div key={t.id} className={`crm-list-card ${selectedTemplate?.id === t.id ? 'selected' : ''}`}
                        style={{ cursor: 'pointer', borderColor: selectedTemplate?.id === t.id ? 'var(--primary)' : undefined }}
                        onClick={() => { setSelectedTemplate(t); setFormData({}); updatePreview(); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <t.icon style={{ color: 'var(--primary)', fontSize: 22 }} />
                          <div className="crm-list-card-title">{t.name}</div>
                        </div>
                        <div className="crm-list-card-sub">{t.category}</div>
                        <div style={{ fontSize: '0.76rem', color: 'var(--ink-soft)', marginTop: 6 }}>{t.description}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: '0.7rem', color: 'var(--muted)' }}>
                          {t.requiresPdf && <span><FaFilePdf /> PDF Required</span>}
                          {t.requiresImage && <span><FaImage /> Image Required</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  {renderTemplateConfig()}
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="crm-filters" style={{ marginBottom: 12 }}>
                    <div className="crm-search-box">
                      <FaSearch className="crm-search-icon" aria-hidden="true" />
                      <input
                        type="text"
                        aria-label="Search recipients"
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search leads..."
                      />
                    </div>
                    <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={selectAllRecipients}>Select All ({leads.length})</button>
                    <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={clearRecipients}>Clear</button>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary)' }}>{selectedIds.length} selected</span>
                  </div>
                  <table className="crm-table">
                    <thead><tr><th style={{ width: 40 }}></th><th>Name</th><th>Company</th><th>Mobile</th><th>Product/Req</th><th>Source</th></tr></thead>
                    <tbody>
                      {filteredLeads.map((r) => (
                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => toggleRecipient(r.id)}>
                          <td><input type="checkbox" className="crm-checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleRecipient(r.id)} /></td>
                          <td><strong>{r.customerName}</strong></td>
                          <td>{r.company || '—'}</td>
                          <td>{r.mobile}</td>
                          <td>{r.product || r.requirement || '—'}</td>
                          <td><span className="crm-badge crm-badge-new" style={{ fontSize: '0.65rem' }}>{r.source || r.provider || 'Manual'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {step === 3 && (
                <div>
                  <div className="crm-panel-card" style={{ marginBottom: 14 }}>
                    <div className="crm-panel-card-title">Campaign Summary</div>
                    <div className="crm-info-row"><span>Name</span><strong>{campaignName || '—'}</strong></div>
                    <div className="crm-info-row"><span>Template</span><strong>{selectedTemplate?.name || '—'}</strong></div>
                    <div className="crm-info-row"><span>Type</span><strong>{selectedTemplate?.type || '—'}</strong></div>
                    <div className="crm-info-row"><span>Recipients</span><strong>{selectedIds.length}</strong></div>
                    <div className="crm-info-row"><span>Valid Leads</span><strong><span style={{ color: 'var(--success)' }}>{filteredLeads.filter(l => selectedIds.includes(l.id)).length}</span></strong></div>
                    <div className="crm-info-row"><span>Schedule</span><strong>{formData.scheduledAt ? fmt(formData.scheduledAt) : 'Immediate'}</strong></div>
                  </div>
                  <div className="crm-panel-card">
                    <div className="crm-panel-card-title">Message Preview</div>
                    {renderPreview()}
                  </div>
                </div>
              )}
            </div>

            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => step === 0 ? setWizard(false) : setStep(step - 1)}>
                {step === 0 ? 'Cancel' : <span><FaArrowLeft aria-hidden="true" /> Back</span>}
              </button>
              {step < 3 ? (
                <button type="button" className="crm-btn crm-btn-primary"
                  onClick={() => {
                    if (!validateStep()) return;
                    setStep(step + 1);
                  }}>Next <FaArrowRight aria-hidden="true" /></button>
              ) : (
                <>
                  {formData.scheduledAt ? (
                    <button type="button" className="crm-btn crm-btn-primary" onClick={scheduleCampaign} disabled={sending}>
                      {sending ? (
                        <span><FaSpinner className="crm-spin" aria-hidden="true" /> Scheduling...</span>
                      ) : (
                        <span><FaCalendarAlt aria-hidden="true" /> Schedule Campaign</span>
                      )}
                    </button>
                  ) : (
                    <button type="button" className="crm-btn crm-btn-primary" onClick={sendCampaign} disabled={sending}>
                      {sending ? (
                        <span><FaSpinner className="crm-spin" aria-hidden="true" /> Sending...</span>
                      ) : (
                        <span><FaPaperPlane aria-hidden="true" /> Send to {selectedIds.length} recipients</span>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}